import dayjs, { Dayjs } from "dayjs";
import { GqlStudent } from "../types";
import { Attendance } from "../types";

type StudentList = GqlStudent[] | [];

/**
 * Compare a date from the BE (ISO or timestamp string) with a datepicker value.
 */
function isSameDay(beDate: string, datepickerValue: Dayjs): boolean {
  const dateNum = Number(beDate);
  const parsed = isNaN(dateNum) ? dayjs(beDate) : dayjs(dateNum);
  return parsed.isValid() && parsed.isSame(datepickerValue, "day");
}

/**
 * pureSortStudents for sorting students list
 */
export function pureSortStudents(students: StudentList = []): StudentList {
  const studentsCopy = [...students];
  return studentsCopy.sort(function (a, b) {
    const aLastName = a.name.split(" ")[1];
    const bLastName = b.name.split(" ")[1];
    let aName = aLastName || a.name;
    let bName = bLastName || b.name;

    return aName.localeCompare(bName, "de", { sensitivity: "case" });
  });
}

/**
 * check if all students HomeWorks are done
 */
export function studentsAllHomeWorksDone(
  students: GqlStudent[] = [],
  datepickerValue: Dayjs
): boolean {
  const nonAbsentStudents = students?.filter(
    (item) =>
      !(item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A)
  );

  if (students?.length) {
    return nonAbsentStudents.length
      ? nonAbsentStudents.every(
          (item) =>
            item?.Homeworks?.length &&
            item?.Homeworks?.some(
              (homework) =>
                homework.done &&
                isSameDay(homework.date, datepickerValue)
            )
        )
      : false;
  } else {
    return false;
  }
}

/**
 * check if all students HomeWorks are UNDONE
 */
export function studentsAllHomeWorksUndone(
  students: GqlStudent[] = [],
  datepickerValue: Dayjs
): boolean {
  const nonAbsentStudents = students?.filter(
    (item) =>
      !(item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A)
  );

  if (students?.length) {
    return nonAbsentStudents.length
      ? nonAbsentStudents.every(
          (item) =>
            item?.Homeworks?.length &&
            item.Homeworks.some(
              (homework) =>
                homework.undone &&
                isSameDay(homework.date, datepickerValue)
            )
        )
      : false;
  } else {
    return false;
  }
}

/**
 * check if all students HomeWorks are NONE
 */
export function studentsAllHomeWorksNoneOrUnset(
  students: GqlStudent[] = []
): boolean {
  const nonAbsentStudents = students?.filter(
    (item) =>
      !(item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A)
  );

  if (students?.length) {
    return nonAbsentStudents.length
      ? nonAbsentStudents.every(
          (item) =>
            item?.Homeworks?.length === 0 ||
            (item?.Homeworks?.length &&
              item?.Homeworks?.every((homework) => homework.none))
        )
      : false;
  } else {
    return false;
  }
}

/**
 * check if all students HomeWorks are UNSET or DEFAULT
 */
export function studentsAllHomeWorksUnset(students: GqlStudent[] = []): boolean {
  const nonAbsentStudents = students?.filter(
    (item) =>
      !(item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A)
  );

  if (students.length) {
    return nonAbsentStudents.length
      ? nonAbsentStudents.every((item) => item?.Homeworks?.length === 0)
      : false;
  } else {
    return false;
  }
}

/**
 * Returns a list with the ids of the student homeworks
 */
export function getHomeWorksIds(
  students: GqlStudent[] | Partial<GqlStudent>[] = []
): { id: string }[] {
  return students
    ?.filter(
      (item) =>
        !(
          item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A
        )
    )
    ?.filter((item) => item?.Homeworks && item?.Homeworks[0]?.id)
    .map((item) => ({
      id: item?.Homeworks && item?.Homeworks[0]?.id,
    }));
}

/**
 * Returns a list with the ids of the students
 */
export function getStudentIds(
  students: GqlStudent[] | Partial<GqlStudent>[] = []
): { studentId: string }[] {
  return students
    ?.filter((item) => item?.Homeworks.length === 0)
    .map((item) => ({
      studentId: item.id,
    }));
}

/**
 * Check if at least one student attendance is empty (non qualified)
 */
export function isOneAttendanceEmpty(students: GqlStudent[] = []): boolean {
  return students.some((item) => item?.Attendances?.length === 0);
}

/**
 * Check if at least one student home work is DONE
 */
export function isOneHomeworkDone(students: GqlStudent[] = []): boolean {
  return students?.length
    ? students
        ?.filter(
          (item) =>
            !(
              item?.Attendances &&
              item?.Attendances[0]?.attendance === Attendance.A
            )
        )
        .some(
          (item) =>
            item?.Homeworks?.length &&
            item?.Homeworks?.some((homework) => homework.done)
        )
    : false;
}

/**
 * Check if at least one student home work is NONE
 */
export function isOneHomeworkNone(students: GqlStudent[] = []): boolean {
  return students?.length
    ? students
        ?.filter(
          (item) =>
            !(
              item?.Attendances &&
              item?.Attendances[0]?.attendance === Attendance.A
            )
        )
        .some(
          (item) =>
            item?.Homeworks?.length &&
            item?.Homeworks?.some((homework) => homework.none)
        )
    : false;
}

/**
 * check if at least one students HomeWorks are UNDONE
 */
export function studentsOneHomeWorksUndone(
  students: GqlStudent[] = [],
  datepickerValue: Dayjs
): boolean {
  return students?.length
    ? students
        ?.filter(
          (item) =>
            !(
              item?.Attendances &&
              item?.Attendances[0]?.attendance === Attendance.A
            )
        )
        .some(
          (item) =>
            item?.Homeworks?.length &&
            item.Homeworks.some(
              (homework) =>
                homework.undone &&
                isSameDay(homework.date, datepickerValue)
            )
        )
    : false;
}

/**
 * Check if all students are absent
 */
export function isAllStudentsAbsent(students: GqlStudent[] = []): boolean {
  return students?.length
    ? students.every(
        (item) =>
          item?.Attendances && item?.Attendances[0]?.attendance === Attendance.A
      )
    : false;
}

/**
 * Check if all students have home works with the same name
 */
export function getHomeWorksName(students: GqlStudent[] = []): string {
  return students?.length
    ? students.find(
        (student) =>
          student?.Homeworks && student?.Homeworks[0]?.name?.length > 0
      )?.Homeworks[0]?.name
    : "";
}
